import React, { useMemo } from "react";
import { View, Pressable, StyleSheet, ActivityIndicator } from "react-native";
import { SealCheckIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import CheckedBadge from "../../svgs/common/CheckedBadge";
import CustomProgressBar from "../../common/general/CustomProgressBar/CustomProgressBar";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { ActivityState } from "@axio/graphql/generated/graphql";
import DueDatePill from "../../customCoursework/Shared/DueDatePill";
import ScheduledPill from "../../customCoursework/Shared/ScheduledPill";

function AiTutorCardProgress({
  icon,
  title,
  isHovered,
  isComplete = false,
  onPress,
  onHoverIn,
  onHoverOut,
  totalObjectives,
  completedObjectives,
  dueDate,
  isReviewContentBeingGenerated,
  isReviewUnavailable,
  activityState,
  scheduledDate,
  isPreviewMode = false,
  onReset,
  aiTutorId,
  isResetting = false,
}) {
  const progressPercentage = totalObjectives > 0 ? (completedObjectives / totalObjectives) * 100 : 0;

  const isModuleLocked = useMemo(
    () => activityState === ActivityState.Scheduled && scheduledDate != null && new Date(scheduledDate) > new Date(),
    [activityState, scheduledDate]
  );

  if (activityState === ActivityState.Draft) {
    return null;
  }

  const reviewStatusText = isReviewContentBeingGenerated
    ? "Review content is being generated"
    : isReviewUnavailable
      ? "Review is not available yet"
      : null;

  return (
    <>
      <Pressable
        onPress={isModuleLocked ? undefined : onPress}
        onHoverIn={onHoverIn}
        onHoverOut={onHoverOut}
        accessibilityLabel={`Open ${title} card`}
        accessibilityRole="button"
        style={({ pressed }) => [
          styles.lessonItem,
          isHovered && styles.lessonItemHovered,
          pressed && styles.lessonItemPressed,
          isModuleLocked && styles.lessonItemLocked,
        ]}
        disabled={isReviewContentBeingGenerated || isReviewUnavailable}>
        <View style={styles.lessonItemLeft}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {reviewStatusText ? (
              <>
                {isReviewContentBeingGenerated ? (
                  <ActivityIndicator size="small" style={{ marginRight: 10 }} color="#3E68FE" />
                ) : (
                  icon && <View style={{ marginRight: 10 }}>{icon}</View>
                )}
                <CustomText
                  style={isModuleLocked && styles.lessonItemTitleLocked}
                  text={reviewStatusText}
                  weight="bold"
                  aria-level="3"
                  role="heading"
                  useTranslationText={false}
                />
              </>
            ) : (
              <>
                {icon && <View style={{ marginRight: 10 }}>{icon}</View>}
                <CustomText
                  text={title}
                  weight="bold"
                  style={[styles.lessonItemTitle, isModuleLocked && styles.lessonItemTitleLocked]}
                  useTranslationText={false}
                  aria-level="3"
                  role="heading"
                />
              </>
            )}
          </View>

          {!reviewStatusText ? (
            <View style={styles.progressContainer}>
              <View style={styles.progressStatus}>
                <View style={styles.progressBar}>
                  <CustomProgressBar
                    ariaLabel="Course progress bar"
                    progress={progressPercentage}
                    color={isComplete ? "#22C55E" : "#3E68FE"}
                    style={isModuleLocked && styles.lessonItemTitleLocked}
                  />
                </View>
                <CustomText
                  text={`${Math.round(progressPercentage)}% Complete`}
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  size="xs"
                  style={isModuleLocked && styles.lessonItemTitleLocked}
                />
                {!isModuleLocked && <DueDatePill dueDate={dueDate} />}
                {isModuleLocked && <ScheduledPill scheduledDate={scheduledDate} />}
              </View>
            </View>
          ) : (
            <View style={styles.statusMeta}>{isModuleLocked && <ScheduledPill scheduledDate={scheduledDate} />}</View>
          )}
        </View>
        <View style={styles.rightSection}>
          {isPreviewMode && onReset && aiTutorId && (
            <CustomButton
              text="Reset Preview"
              onPress={() => onReset(aiTutorId)}
              styleType="primaryTransparentStroke"
              size="s"
              leftIcon={<ArrowClockwiseIcon size={14} />}
              style={styles.resetButton}
              loading={isResetting}
              disabled={isResetting}
              useTranslationText={false}
              accessibilityLabel="Reset preview progress for this Interactive Learning Experience"
            />
          )}
          {!isReviewContentBeingGenerated &&
            (isComplete ? <CheckedBadge /> : <SealCheckIcon size={24} color="#525457" />)}
        </View>
      </Pressable>
    </>
  );
}

const styles = StyleSheet.create({
  progressBar: {
    width: 150,
    maxWidth: 150,
  },
  lessonItem: {
    marginVertical: 8,
    marginHorizontal: 1,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 75,
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    outlineWidth: 0,
    outlineColor: "transparent",
    outlineStyle: "solid",
    transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  lessonItemLocked: {
    cursor: "auto",
  },
  lessonItemHovered: {
    outlineWidth: 2,
    outlineColor: "#D0D5DD",
    outlineStyle: "solid",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  lessonItemPressed: {
    backgroundColor: "#F5F5F5",
  },
  lessonItemLeft: {
    flex: 1,
    marginRight: 16,
  },
  lessonItemTitle: {
    marginBottom: 4,
  },
  lessonItemTitleLocked: {
    opacity: 0.5,
  },
  progressContainer: {
    marginTop: 4,
    gap: 8,
  },
  statusMeta: {
    marginTop: 4,
    gap: 8,
  },
  progressStatus: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 12,
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  resetButton: {
    marginRight: 0,
  },
});

export default AiTutorCardProgress;
